import { Drawer, flash, SubmitButton } from "@xola/ui-kit";
import { useUnmount } from "ahooks";
import { FormField, SecondaryButton, SwitchField } from "common";
import { FormikProps } from "formik";
import React, { useRef } from "react";
import { useMutation } from "react-query";
import { Merchandise, MerchandiseSource, Permission, PurchaseRule } from "types";
import { array, number, object } from "yup";
import { DownDepositSelector } from "../pricing/DownDepositSelector";
import { getPricingInitialValues, MerchandisePricingFormValues } from "./merchandiseHelper";
import { MerchandiseTaxesAndFees } from "./MerchandiseTaxesAndFees";
import { api } from "@/api/api";
import { Form } from "@/components/Common/Form";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { EditTaxAndFee } from "@/components/Settings/TaxesAndFees/EditTaxAndFee";
import { ScrollToFieldError } from "@/helpers/validationHelper";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useModal } from "@/hooks/useVisibility";
import { MERCHANDISE_API_PATH, MERCHANDISE_CACHE_KEY } from "@/models/Merchandise";
import { updateCache } from "@/utils/updateCache";

const purchaseSourceValues = Object.values(MerchandiseSource);

const validationSchema = object().shape({
    allowedSources: array().min(1, "At least one source is required"),
    downDeposit: object()
        .default(null)
        .nullable()
        .shape({
            amount: number().nullable().min(0.01, "Amount must be greater than 0").required("Amount is required"),
            days: number().nullable().min(0, "Days must be 0 or greater"),
        }),
});

export const MerchandisePricing = ({ merchandise }: { merchandise: Merchandise }) => {
    const formikRef = useRef<FormikProps<MerchandisePricingFormValues>>(null);
    const { isGranted } = useAuthorization();
    const canViewTaxesAndFees = isGranted(Permission.NON_WARE_MANAGER_VIEW);
    const taxDrawer = useModal();
    const selectedPurchaseRuleRef = useRef<PurchaseRule | null>(null);
    const selectedPurchaseRuleLinkIdRef = useRef<ID>();
    const shouldIgnoreDrawerCloseRef = useRef(false);
    const resetDrawerCloseGuardTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const { mutateAsync: savePricing } = useMutation(async (payload: MerchandisePricingFormValues) => {
        const { data } = await api.put<Merchandise>(`${MERCHANDISE_API_PATH}/${merchandise?.id}`, payload);
        flash.success("Pricing saved successfully");
        updateCache(MERCHANDISE_CACHE_KEY, data);
        return data;
    });

    const onSave = async (values: MerchandisePricingFormValues) => {
        await savePricing(values);
    };

    const handleDrawerContentInteraction = () => {
        shouldIgnoreDrawerCloseRef.current = true;

        if (resetDrawerCloseGuardTimeoutRef.current) {
            clearTimeout(resetDrawerCloseGuardTimeoutRef.current);
        }

        resetDrawerCloseGuardTimeoutRef.current = setTimeout(() => {
            shouldIgnoreDrawerCloseRef.current = false;
        }, 0);
    };

    const handleDrawerClose = () => {
        if (shouldIgnoreDrawerCloseRef.current) {
            shouldIgnoreDrawerCloseRef.current = false;
            return;
        }

        taxDrawer.hide();
    };

    const handleManageTaxesAndFees = () => {
        selectedPurchaseRuleRef.current = null;
        selectedPurchaseRuleLinkIdRef.current = undefined;
        taxDrawer.show();
    };

    useUnmount(() => {
        if (resetDrawerCloseGuardTimeoutRef.current) {
            clearTimeout(resetDrawerCloseGuardTimeoutRef.current);
        }
    });

    // Open drawer in edit mode with selected rule context.
    const handleEditTaxPurchaseRule = (purchaseRule: PurchaseRule, purchaseRuleLinkId?: ID) => {
        selectedPurchaseRuleRef.current = purchaseRule;
        selectedPurchaseRuleLinkIdRef.current = purchaseRuleLinkId;
        taxDrawer.show();
    };

    return (
        <>
            <XolaFormik
                enableReinitialize
                initialValues={getPricingInitialValues(merchandise)}
                validationSchema={validationSchema}
                innerRef={formikRef}
                onSubmit={onSave}
            >
                {({ isSubmitting, values, setFieldValue, errors, handleReset, isValid }) => {
                    const handleSourceChange = (isChecked: boolean, value: string) => {
                        setFieldValue(
                            "allowedSources",
                            getSelectedPurchaseSource(isChecked, values.allowedSources, value),
                        );
                    };

                    return (
                        <Form>
                            <ScrollToFieldError />
                            <ContentLayout isWide>
                                <div className="space-y-8">
                                    <div className="space-y-4">
                                        <h3>Payments, Channels & Fees</h3>

                                        {merchandise && (
                                            <DownDepositSelector
                                                experience={merchandise}
                                                value={values}
                                                errors={errors}
                                                perPersonLabel="per purchasable item"
                                                perBookingLabel="per purchase"
                                                showDepositDays={false}
                                                title="Down Deposit"
                                                description="Allow customers to pay a deposit at checkout, with the remaining balance due at the time of fulfillment."
                                                onChange={setFieldValue}
                                            />
                                        )}

                                        <FormField
                                            className="space-y-2"
                                            classNames={{ label: "text-md" }}
                                            name="allowedSources"
                                            label="Item Available to Purchase From"
                                        >
                                            {purchaseSourceValues.map((value) => (
                                                <div key={value}>
                                                    <SwitchField
                                                        isChecked={values.allowedSources.includes(value)}
                                                        onChange={(isChecked) => handleSourceChange(isChecked, value)}
                                                    >
                                                        <span className="capitalize">
                                                            {value === MerchandiseSource.PLUGIN
                                                                ? "Apps & Integrations"
                                                                : value.replace("_", " ")}
                                                        </span>
                                                    </SwitchField>
                                                </div>
                                            ))}
                                        </FormField>
                                    </div>

                                    {canViewTaxesAndFees && (
                                        <MerchandiseTaxesAndFees
                                            merchandiseId={merchandise.id}
                                            onManage={handleManageTaxesAndFees}
                                            onEditTaxPurchaseRule={handleEditTaxPurchaseRule}
                                        />
                                    )}
                                </div>
                                <ContentLayout.Footer preferenceSidebar>
                                    <SubmitButton isLoading={isSubmitting} disabled={!isValid}>
                                        Save
                                    </SubmitButton>
                                    <SecondaryButton type="reset" onClick={handleReset}>
                                        Cancel
                                    </SecondaryButton>
                                </ContentLayout.Footer>
                            </ContentLayout>
                        </Form>
                    );
                }}
            </XolaFormik>

            <Drawer
                size="xl"
                title="Taxes & Fees"
                isOpen={taxDrawer.isVisible}
                content={
                    <div
                        onMouseDownCapture={handleDrawerContentInteraction}
                        onTouchStartCapture={handleDrawerContentInteraction}
                    >
                        <EditTaxAndFee
                            isInDrawer
                            purchaseRule={selectedPurchaseRuleRef.current}
                            purchaseRuleLinkId={selectedPurchaseRuleLinkIdRef.current}
                            defaultMerchandiseId={selectedPurchaseRuleRef.current ? undefined : merchandise.id}
                            onClose={taxDrawer.hide}
                        />
                    </div>
                }
                onClose={handleDrawerClose}
            />
        </>
    );
};

function getSelectedPurchaseSource(isChecked: boolean, purchaseSources: string[], value: string): string[] {
    return isChecked ? [...purchaseSources, value] : purchaseSources.filter((item) => item !== value);
}
